import { useCallback } from "react"

import {
  transportOperations,
  useTransportMutation,
  useTransportPaginatedQuery,
  useTransportQueryUtils,
} from "@/transport"

import type { ListReviewMetricSkipsRequest } from "@/types_api/backend/transport/list_review_metric_skips_request"
import type {
  ListReviewMetricSkipsResponse,
  ReviewMetricSkip,
} from "@/types_api/backend/transport/list_review_metric_skips_response"

import { useReviewDisagreementConversationRecheck } from "./reviewDisagreementStateTransport"

export type { ReviewMetricSkip }

const METRIC_SKIP_PAGE_SIZE = 100

/** Loads every reviewer's current metric skips for one review conversation. */
export function useReviewMetricSkipsTransport({
  projectId,
  simulationOutputId,
  enabled = true,
}: {
  projectId: string | null | undefined
  simulationOutputId: string | null | undefined
  enabled?: boolean
}) {
  const request: ListReviewMetricSkipsRequest = {
    project_id: projectId ?? "",
    simulation_output_id: simulationOutputId ?? "",
    page_size: METRIC_SKIP_PAGE_SIZE,
  }
  return useTransportPaginatedQuery({
    operation: "listReviewMetricSkips",
    request,
    getNextRequest: (
      page: ListReviewMetricSkipsResponse,
      previousRequest: ListReviewMetricSkipsRequest | undefined,
    ) =>
      page.next_page_token
        ? { ...(previousRequest ?? request), page_token: page.next_page_token }
        : null,
    combinePages: (pages: ListReviewMetricSkipsResponse[]) =>
      pages.flatMap((page) => page.skips),
    options: {
      enabled: Boolean(projectId && simulationOutputId) && enabled,
    },
  })
}

interface MetricSkipWrite {
  projectId: string
  simulationOutputId: string
  metricId: string
}

/**
 * Skip and Unskip act on the signed-in reviewer's own skip. Each resolves once
 * the reads it affects have refreshed, so callers can show a pending state
 * until the card reflects the server.
 */
export function useReviewMetricSkipMutationsTransport() {
  const transportQueryUtils = useTransportQueryUtils()
  const recheckDisagreementState = useReviewDisagreementConversationRecheck()
  const { mutateAsync: skip } = useTransportMutation("skipReviewMetric")
  const { mutateAsync: unskip } = useTransportMutation("unskipReviewMetric")

  const writeThenRefresh = useCallback(
    async (
      simulationOutputId: string,
      projectId: string,
      write: () => Promise<unknown>,
    ) => {
      try {
        await write()
      } finally {
        await Promise.all([
          recheckDisagreementState(simulationOutputId, projectId),
          transportQueryUtils.invalidateCacheTags(
            transportOperations.skipReviewMetric.cacheTags,
          ),
        ])
      }
    },
    [recheckDisagreementState, transportQueryUtils],
  )

  const skipMetric = useCallback(
    ({ projectId, simulationOutputId, metricId }: MetricSkipWrite) =>
      writeThenRefresh(simulationOutputId, projectId, () =>
        skip({
          project_id: projectId,
          simulation_output_id: simulationOutputId,
          metric_id: metricId,
        }),
      ),
    [skip, writeThenRefresh],
  )

  const unskipMetric = useCallback(
    ({ projectId, simulationOutputId, metricId }: MetricSkipWrite) =>
      writeThenRefresh(simulationOutputId, projectId, () =>
        unskip({
          project_id: projectId,
          simulation_output_id: simulationOutputId,
          metric_id: metricId,
        }),
      ),
    [unskip, writeThenRefresh],
  )

  return { skipMetric, unskipMetric }
}
